import { useState, useEffect } from "react";
import axios from "axios";
import { useOutletContext } from 'react-router-dom';
import { toast } from "react-toastify";
import { useAuth } from "../../../context/auth-context";
import { rootUrl, getAllConstituencies, formatToZMW} from "../../../helpers";
import { zambiaProvinces } from "../../../data/locations";
import { GrProjects } from "react-icons/gr";
import { FaEdit, FaFolderOpen, FaPlus, FaTimes, FaTrash, FaFileExcel, FaChartLine } from "react-icons/fa";
import Select from "react-select";
import FormGenerator from "../../../components/formGenerator";
import * as XLSX from 'xlsx';

const ProjectPage = () => {
  const [fields, setFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [editId, setEditId] = useState(null);
  const [addButton, setAddButton] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

    const {allData, setAllData} = useOutletContext();
    const auth = useAuth();

    const  constituencies  = getAllConstituencies(zambiaProvinces);

     useEffect(() => {
          if (allData?.budget) {
            setBudgets(allData.budget);
          }
        }, [allData]); 

  useEffect(() => {
    if (allData.head) {
      setFields([
        {
          type: "select",
          name: "b_id",
          label: "Budget",
          required: true,
          options: allData.budget.map((b) => ({
            value: b.id,
            label: `${b.coa} - ${allData.program.find(a=>a.programCode == b.program)?.name || ''} -
            ${allData.subProgram.find(a=>a.subProgramCode == b.subprogram
              && a.programCode == b.program
              )?.name  || ''} 
            - ${b.toy}`,
          })),
        },
        {
          type: "select",
          name: "constituency",
          label: "Constituency",
          required: true,
          options: constituencies.map((res) => ({
            value: res,
            label: res,
          })),
        },
        {
            type: 'select',
            name: 'head',
            label: 'Head',
            required: true,
            dependentFields: ['sub'],
            options: allData.head.map(res => ({
              value: res.id,
              label: res.name,
            })),
          },
          {
            type: 'select',
            name: 'sub',
            label: 'Sub',
            required: true,
            options: allData.sub.map(res => ({
              value: res.id,
              label: res.name,
              dependsOn: 'head',
              dependentValue: res.pid,
            })),
          },
        {
          type: "text",
          name: "name",
          label: "Name",
          required: true,
        },
        {
          type: "text",
          name: "amount",
          label: "Amount",
          required: true,
        },
        {
          type: "text",
          name: "year",
          label: "Project Year",
          required: true,
        },
        {
          type: "textarea",
          name: "description",
          label: "Description",
        },
      ]);
    }
    fetchProjects();
  }, [allData]);

  const fetchProjects = async () => {
    try {
      const resp = await axios.get(rootUrl(`/getProjs/${auth.user.id}`));
      setProjects(resp.data.data);
    } catch (e) {
      toast.error("Error fetching projects");
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddProject = async (payload) => {
    
    setAddButton(true);
    const data = { ...payload, uid: auth.user.id };
    try {
      await axios.post(rootUrl("/addProj"), data);
      toast.success("Project added successfully");
      fetchProjects();
      toggleModal()
      setFormData({});
      setAddButton(false);
    } catch (e) {
      toast.error("Error adding project");
    }
  };

  const handleEditProject = async (payload) => {
    
    const data = { ...payload, uid: auth.user.id };

    try {
      await axios.post(rootUrl(`/updateProj`), data);
      toast.success("Project updated successfully");
      fetchProjects();
      setFormData({});
      toggleModal()
      setEditId(null);
    } catch (e) {
      toast.error("Error updating project");
    }
  };

  const handleEdit = (project) => {
    // Map head name to ID
    const headObj = allData.head.find(h => h.name === project.head);
    // Map sub name to ID
    const subObj = allData.sub.find(s => s.name === project.sub);

    // Create a new object with IDs for head and sub
    const projectWithIds = {
      ...project,
      head: headObj ? headObj.id : '',
      sub: subObj ? subObj.id : '',
    };

    setFormData(projectWithIds);
    setSelectedProject(projectWithIds);
    toggleModal();
    setEditId(project.id);
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(rootUrl(`/deleteProj/${deleteId}`));
      toast.success("Project deleted successfully");
      fetchProjects();
    } catch (e) {
      toast.error("Error deleting project");
    } finally {
      setShowDeleteModal(false);
    }
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const toggleModal = () => {
    setIsModalOpen((prev) => !prev);
    setAddButton(false)
    }

    const [searchTerm, setSearchTerm] = useState("");

    const filteredProjects = projects.filter((proj) =>
  Object.values(proj).some((value) =>
    value !== null && value !== undefined && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
  )
);
  

  // Export to Excel function
  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      filteredProjects.map((proj) => ({
        Budget: budgets?.find(a=>a.id == proj.b_id)?.coa + ' ' + budgets?.find(a=>a.id == proj.b_id)?.toy,
        Name: proj.name,
        Head: proj.head,
        SubHead: proj.sub,
        Description: proj.description,
        Amount: proj.amount,
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Projects');
    XLSX.writeFile(wb, 'projects.xlsx');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-950 to-blue-800 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 rounded-xl p-3">
              <GrProjects className="text-2xl" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Projects</h1>
              <p className="text-blue-100 text-sm mt-1">
                Track and manage budget-linked development projects.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={exportToExcel}
              className="inline-flex items-center bg-emerald-600 text-white h-9 px-3 rounded-lg text-xs md:text-sm font-medium hover:bg-emerald-700 transition-colors"
            >
              <FaFileExcel className="mr-2" /> Export to Excel
            </button>
            <button
              onClick={toggleModal}
              className="inline-flex items-center bg-white/20 hover:bg-white/30 text-white h-9 px-3 rounded-lg text-xs md:text-sm font-medium transition-colors border border-white/30"
            >
              <FaPlus className="mr-1" />
              Add Project
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full flex flex-col bg-white min-h-[400px] rounded-2xl p-4 md:p-6 shadow-sm border border-gray-100">

        {/* Search + summary */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div className="w-full md:w-72">
          <input
            type="text"
            placeholder="Search projects..."
            className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="text-xs md:text-sm text-gray-600 md:text-right">
          Showing{" "}
          <span className="font-semibold">
            {filteredProjects.length}
          </span>{" "}
          project{filteredProjects.length === 1 ? "" : "s"}
        </div>
      </div>

        {/* Table */}
        <div className="mt-2 rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead className="bg-blue-950 text-white">
              <tr>
                <th className="border border-blue-900/40 px-3 py-2 text-left text-xs md:text-sm font-medium">
                  #
                </th>
                <th className="border border-blue-900/40 px-3 py-2 text-left text-xs md:text-sm font-medium">
                  Budget
                </th>
                <th className="border border-blue-900/40 px-3 py-2 text-left text-xs md:text-sm font-medium">
                  Name
                </th>
                <th className="border border-blue-900/40 px-3 py-2 text-left text-xs md:text-sm font-medium">
                  Head
                </th>
                <th className="border border-blue-900/40 px-3 py-2 text-left text-xs md:text-sm font-medium">
                  Sub head
                </th>
                <th className="border border-blue-900/40 px-3 py-2 text-left text-xs md:text-sm font-medium">
                  Description
                </th>
                <th className="border border-blue-900/40 px-3 py-2 text-right text-xs md:text-sm font-medium">
                  Amount
                </th>
                <th className="border border-blue-900/40 px-3 py-2 text-center text-xs md:text-sm font-medium">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredProjects.length > 0 ? (
                filteredProjects.map((proj, index) => (
                  <tr
                    key={proj.id}
                    className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="border border-gray-200 px-3 py-2 text-xs md:text-sm">
                      {index + 1}
                    </td>
                    <td className="border border-gray-200 px-3 py-2 text-xs md:text-sm">
                      {budgets?.find(a => a.id == proj.b_id)?.coa}{" "}
                      {budgets?.find(a => a.id == proj.b_id)?.toy}
                    </td>
                    <td className="border border-gray-200 px-3 py-2 text-xs md:text-sm">
                      {proj.name}
                    </td>
                    <td className="border border-gray-200 px-3 py-2 text-xs md:text-sm">
                      {proj.head}
                    </td>
                    <td className="border border-gray-200 px-3 py-2 text-xs md:text-sm">
                      {proj.sub}
                    </td>
                    <td className="border border-gray-200 px-3 py-2 text-xs md:text-sm">
                      {proj.description}
                    </td>
                    <td className="border border-gray-200 px-3 py-2 text-xs md:text-sm font-semibold text-right">
                      {formatToZMW(proj.amount)}
                    </td>
                    <td className="border border-gray-200 px-3 py-2 text-center">
                      <button
                        onClick={() => handleEdit(proj)}
                        className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 text-sm"
                      >
                        <FaEdit />
                      </button>
                      {/* <button
                        onClick={() => handleDeleteClick(proj.id)}
                        className="ml-2 inline-flex items-center justify-center h-8 w-8 rounded-full bg-red-50 text-red-600 hover:bg-red-100 text-sm"
                      >
                        <FaTrash />
                      </button> */}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    className="text-center p-6 text-gray-500 bg-white"
                  >
                    <div className="flex flex-col justify-center items-center">
                      <FaFolderOpen className="text-gray-400 text-5xl mb-2" />
                      <p className="text-sm text-gray-600">
                        No projects found for the current filters.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
            {/* Total Amount Row */}
            <tfoot>
              <tr className="bg-gray-100 font-semibold text-sm">
                <td
                  colSpan="6"
                  className="text-right px-3 py-2 text-gray-700"
                >
                  Total Amount
                </td>
                <td className="px-3 py-2 text-right text-gray-900">
                  {formatToZMW(
                    filteredProjects.reduce(
                      (sum, proj) => sum + parseFloat(proj.amount || 0),
                      0
                    )
                  )}
                </td>
                <td className="px-3 py-2" />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

        {/* Add / Edit Modal */}
        {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-4 md:p-6 w-full max-w-3xl shadow-xl">
            {/* Modal Header */}
            <div className="flex justify-between items-center mb-4 border-b border-gray-200 pb-3">
              <h2 className="text-lg md:text-xl font-semibold text-gray-800">
                {editId ? "Edit Project" : "Add Project"}
              </h2>
              <button
                onClick={toggleModal}
                className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-red-50 text-red-600 hover:bg-red-100"
              >
                <FaTimes size={16} />
              </button>
            </div>
            {/* Modal Body */}
            <FormGenerator
              fields={fields}
              initialValues={editId ? selectedProject : {}}
              onSubmit={editId ? handleEditProject : handleAddProject}
            />
          </div>
        </div>
      )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white p-5 md:p-6 rounded-2xl shadow-xl max-w-sm w-full">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Confirm Deletion
            </h3>
            <p className="text-sm text-gray-600">
              Are you sure you want to delete this project?
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={confirmDelete}
                className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700"
              >
                Yes, Delete
              </button>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ProjectPage;
